import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Footer from '../components/common/Footer';

const legalContent = {
  fr: {
    privacyPolicy: {
      title: 'Politique de Confidentialité',
      lastUpdated: 'Dernière mise à jour : 17 septembre 2026',
      sections: [
        {
          title: '1. Collecte des données',
          content: `PilotLeague collecte les données suivantes pour fournir ses services :
          
• **Données de compte** : email, nom d'utilisateur, mot de passe hashé
• **Données de vol** : sessions de vol, scores, statistiques de performance
• **Données techniques** : adresse IP, type de navigateur, logs de connexion

Ces données sont collectées avec votre consentement explicite lors de l'inscription.`,
        },
        {
          title: '2. Utilisation des données',
          content: `Vos données sont utilisées pour :

• Fournir les services de scoring et d'analyse de vol
• Améliorer votre expérience utilisateur
• Assurer la sécurité de votre compte
• Communiquer avec vous concernant votre compte : e-mails de service indispensables (réinitialisation de mot de passe, réponses du support, incidents affectant vos données) et, quelques fois par an, informations de service et nouveautés du produit

Vous pouvez vous désinscrire des informations de service et nouveautés du produit à tout moment, en un clic depuis chaque e-mail ou dans Réglages → E-mails. Les e-mails indispensables au fonctionnement de votre compte restent envoyés. Nous ne vendons jamais vos données personnelles à des tiers et n'envoyons aucune publicité pour des tiers.`,
        },
        {
          title: '3. Conservation des données',
          content: `• Les données de compte sont conservées tant que votre compte est actif
• Les données de vol sont conservées pendant 5 ans
• Les logs de connexion sont conservés pendant 1 an
• Vous pouvez demander la suppression de vos données à tout moment`,
        },
        {
          title: '4. Vos droits (RGPD)',
          content: `Conformément au RGPD, vous disposez des droits suivants :
          
• **Droit d'accès** : obtenir une copie de vos données
• **Droit de rectification** : corriger vos données inexactes
• **Droit à l'effacement** : supprimer vos données
• **Droit à la portabilité** : exporter vos données
• **Droit d'opposition** : vous opposer au traitement

Pour exercer ces droits, contactez-nous à privacy@pilotleague.com`,
        },
        {
          title: '5. Contact',
          content: `Pour toute question concernant cette politique :
          
PilotLeague
Email : privacy@pilotleague.com`,
        },
      ],
    },
    termsOfService: {
      title: "Conditions d'Utilisation",
      lastUpdated: 'Dernière mise à jour : 12 décembre 2025',
      sections: [
        {
          title: '1. Acceptation des conditions',
          content: `En créant un compte PilotLeague, vous acceptez les présentes conditions d'utilisation. Si vous n'acceptez pas ces conditions, veuillez ne pas utiliser nos services.`,
        },
        {
          title: '2. Description du service',
          content: `PilotLeague est une plateforme d'analyse et de scoring de vols pour les simulateurs de vol. Le service permet de :
          
• Capturer et analyser les données de vol
• Obtenir des scores de performance
• Comparer vos performances avec d'autres pilotes
• Suivre votre progression`,
        },
        {
          title: '3. Compte utilisateur',
          content: `• Vous êtes responsable de la confidentialité de votre mot de passe
• Vous devez fournir des informations exactes
• Un seul compte par personne est autorisé
• Les comptes inactifs pendant plus de 2 ans peuvent être supprimés`,
        },
        {
          title: '4. Utilisation acceptable',
          content: `Vous vous engagez à ne pas :
          
• Tricher ou manipuler les données de vol
• Utiliser le service de manière illégale
• Partager votre compte avec d'autres personnes
• Tenter d'accéder aux données d'autres utilisateurs`,
        },
        {
          title: '5. Propriété intellectuelle',
          content: `Tous les contenus du site (logos, textes, code) sont la propriété de PilotLeague et protégés par le droit d'auteur. Vos données de vol vous appartiennent.`,
        },
        {
          title: '6. Limitation de responsabilité',
          content: `PilotLeague est fourni "tel quel". Nous ne garantissons pas que le service sera ininterrompu ou exempt d'erreurs. Notre responsabilité est limitée au montant de votre abonnement.`,
        },
      ],
    },
    cookiePolicy: {
      title: 'Politique des Cookies',
      lastUpdated: 'Dernière mise à jour : 12 décembre 2025',
      sections: [
        {
          title: "1. Qu'est-ce qu'un cookie ?",
          content: `Un cookie est un petit fichier texte stocké sur votre appareil lorsque vous visitez un site web. Il permet au site de mémoriser vos préférences.`,
        },
        {
          title: '2. Cookies utilisés',
          content: `**Cookies essentiels** (toujours actifs) :
• Session d'authentification
• Préférences de langue
• Token de sécurité CSRF

**Cookies analytiques** (optionnels) :
• Analyse d'utilisation anonyme
• Performance du site`,
        },
        {
          title: '3. Gestion des cookies',
          content: `Vous pouvez contrôler les cookies via les paramètres de votre navigateur. Désactiver les cookies essentiels peut affecter le fonctionnement du site.`,
        },
      ],
    },
  },
  en: {
    privacyPolicy: {
      title: 'Privacy Policy',
      lastUpdated: 'Last updated: September 17, 2026',
      sections: [
        {
          title: '1. Data Collection',
          content: `PilotLeague collects the following data to provide its services:
          
• **Account data**: email, username, hashed password
• **Flight data**: flight sessions, scores, performance statistics
• **Technical data**: IP address, browser type, connection logs

This data is collected with your explicit consent during registration.`,
        },
        {
          title: '2. Data Usage',
          content: `Your data is used to:

• Provide flight scoring and analysis services
• Improve your user experience
• Ensure your account security
• Communicate with you regarding your account: essential service e-mails (password reset, support replies, incidents affecting your data) and, a few times a year, service notices and product news

You can opt out of service notices and product news at any time, in one click from any e-mail or in Settings → E-mails. E-mails required to operate your account are still sent. We never sell your personal data to third parties and never send third-party advertising.`,
        },
        {
          title: '3. Data Retention',
          content: `• Account data is retained as long as your account is active
• Flight data is retained for 5 years
• Connection logs are retained for 1 year
• You can request deletion of your data at any time`,
        },
        {
          title: '4. Your Rights (GDPR)',
          content: `Under GDPR, you have the following rights:
          
• **Right of access**: obtain a copy of your data
• **Right to rectification**: correct inaccurate data
• **Right to erasure**: delete your data
• **Right to portability**: export your data
• **Right to object**: object to processing

To exercise these rights, contact us at privacy@pilotleague.com`,
        },
        {
          title: '5. Contact',
          content: `For any questions regarding this policy:
          
PilotLeague
Email: privacy@pilotleague.com`,
        },
      ],
    },
    termsOfService: {
      title: 'Terms of Service',
      lastUpdated: 'Last updated: December 12, 2025',
      sections: [
        {
          title: '1. Acceptance of Terms',
          content: `By creating a PilotLeague account, you agree to these terms of service. If you do not accept these terms, please do not use our services.`,
        },
        {
          title: '2. Service Description',
          content: `PilotLeague is a flight analysis and scoring platform for flight simulators. The service allows you to:
          
• Capture and analyze flight data
• Obtain performance scores
• Compare your performance with other pilots
• Track your progress`,
        },
        {
          title: '3. User Account',
          content: `• You are responsible for keeping your password confidential
• You must provide accurate information
• Only one account per person is allowed
• Accounts inactive for more than 2 years may be deleted`,
        },
        {
          title: '4. Acceptable Use',
          content: `You agree not to:
          
• Cheat or manipulate flight data
• Use the service illegally
• Share your account with others
• Attempt to access other users' data`,
        },
        {
          title: '5. Intellectual Property',
          content: `All site content (logos, text, code) is the property of PilotLeague and protected by copyright. Your flight data belongs to you.`,
        },
        {
          title: '6. Limitation of Liability',
          content: `PilotLeague is provided "as is". We do not guarantee that the service will be uninterrupted or error-free. Our liability is limited to the amount of your subscription.`,
        },
      ],
    },
    cookiePolicy: {
      title: 'Cookie Policy',
      lastUpdated: 'Last updated: December 12, 2025',
      sections: [
        {
          title: '1. What is a cookie?',
          content: `A cookie is a small text file stored on your device when you visit a website. It allows the site to remember your preferences.`,
        },
        {
          title: '2. Cookies Used',
          content: `**Essential cookies** (always active):
• Authentication session
• Language preferences
• CSRF security token

**Analytics cookies** (optional):
• Anonymous usage analysis
• Site performance`,
        },
        {
          title: '3. Cookie Management',
          content: `You can control cookies through your browser settings. Disabling essential cookies may affect site functionality.`,
        },
      ],
    },
  },
  de: {
    privacyPolicy: {
      title: 'Datenschutzrichtlinie',
      lastUpdated: 'Letzte Aktualisierung: 12. Dezember 2025',
      sections: [
        {
          title: '1. Datenerhebung',
          content: `PilotLeague erhebt folgende Daten zur Bereitstellung seiner Dienste:
          
• **Kontodaten**: E-Mail, Benutzername, gehashtes Passwort
• **Flugdaten**: Flugsitzungen, Bewertungen, Leistungsstatistiken
• **Technische Daten**: IP-Adresse, Browsertyp, Verbindungsprotokolle`,
        },
        {
          title: '2. Datennutzung',
          content: `Ihre Daten werden verwendet für:
          
• Bereitstellung von Flugbewertungs- und Analysediensten
• Verbesserung Ihrer Benutzererfahrung
• Gewährleistung Ihrer Kontosicherheit
• Kommunikation bezüglich Ihres Kontos`,
        },
        {
          title: '3. Ihre Rechte (DSGVO)',
          content: `Nach der DSGVO haben Sie folgende Rechte:
          
• **Auskunftsrecht**: Eine Kopie Ihrer Daten erhalten
• **Recht auf Berichtigung**: Ungenaue Daten korrigieren
• **Recht auf Löschung**: Ihre Daten löschen
• **Recht auf Datenübertragbarkeit**: Ihre Daten exportieren

Kontakt: privacy@pilotleague.com`,
        },
      ],
    },
    termsOfService: {
      title: 'Nutzungsbedingungen',
      lastUpdated: 'Letzte Aktualisierung: 12. Dezember 2025',
      sections: [
        {
          title: '1. Akzeptanz der Bedingungen',
          content: `Durch die Erstellung eines PilotLeague-Kontos stimmen Sie diesen Nutzungsbedingungen zu.`,
        },
        {
          title: '2. Dienstbeschreibung',
          content: `PilotLeague ist eine Fluganalyse- und Bewertungsplattform für Flugsimulatoren.`,
        },
        {
          title: '3. Akzeptable Nutzung',
          content: `Sie verpflichten sich, nicht zu:
          
• Betrügen oder Flugdaten manipulieren
• Den Dienst illegal nutzen
• Ihr Konto mit anderen teilen`,
        },
      ],
    },
    cookiePolicy: {
      title: 'Cookie-Richtlinie',
      lastUpdated: 'Letzte Aktualisierung: 12. Dezember 2025',
      sections: [
        {
          title: '1. Was ist ein Cookie?',
          content: `Ein Cookie ist eine kleine Textdatei, die auf Ihrem Gerät gespeichert wird, wenn Sie eine Website besuchen.`,
        },
        {
          title: '2. Verwendete Cookies',
          content: `**Wesentliche Cookies** (immer aktiv):
• Authentifizierungssitzung
• Spracheinstellungen
• CSRF-Sicherheitstoken`,
        },
      ],
    },
  },
  es: {
    privacyPolicy: {
      title: 'Política de Privacidad',
      lastUpdated: 'Última actualización: 12 de diciembre de 2025',
      sections: [
        {
          title: '1. Recopilación de datos',
          content: `PilotLeague recopila los siguientes datos para proporcionar sus servicios:
          
• **Datos de cuenta**: correo electrónico, nombre de usuario, contraseña hasheada
• **Datos de vuelo**: sesiones de vuelo, puntuaciones, estadísticas de rendimiento
• **Datos técnicos**: dirección IP, tipo de navegador, registros de conexión`,
        },
        {
          title: '2. Uso de datos',
          content: `Sus datos se utilizan para:
          
• Proporcionar servicios de puntuación y análisis de vuelos
• Mejorar su experiencia de usuario
• Garantizar la seguridad de su cuenta`,
        },
        {
          title: '3. Sus derechos (RGPD)',
          content: `Según el RGPD, tiene los siguientes derechos:
          
• **Derecho de acceso**: obtener una copia de sus datos
• **Derecho de rectificación**: corregir datos inexactos
• **Derecho de supresión**: eliminar sus datos

Contacto: privacy@pilotleague.com`,
        },
      ],
    },
    termsOfService: {
      title: 'Términos de Servicio',
      lastUpdated: 'Última actualización: 12 de diciembre de 2025',
      sections: [
        {
          title: '1. Aceptación de términos',
          content: `Al crear una cuenta PilotLeague, acepta estos términos de servicio.`,
        },
        {
          title: '2. Descripción del servicio',
          content: `PilotLeague es una plataforma de análisis y puntuación de vuelos para simuladores de vuelo.`,
        },
      ],
    },
    cookiePolicy: {
      title: 'Política de Cookies',
      lastUpdated: 'Última actualización: 12 de diciembre de 2025',
      sections: [
        {
          title: '1. ¿Qué es una cookie?',
          content: `Una cookie es un pequeño archivo de texto almacenado en su dispositivo cuando visita un sitio web.`,
        },
      ],
    },
  },
  it: {
    privacyPolicy: {
      title: 'Informativa sulla Privacy',
      lastUpdated: 'Ultimo aggiornamento: 12 dicembre 2025',
      sections: [
        {
          title: '1. Raccolta dati',
          content: `PilotLeague raccoglie i seguenti dati per fornire i suoi servizi:
          
• **Dati account**: email, nome utente, password hashata
• **Dati di volo**: sessioni di volo, punteggi, statistiche prestazioni
• **Dati tecnici**: indirizzo IP, tipo di browser, log di connessione`,
        },
        {
          title: '2. I tuoi diritti (GDPR)',
          content: `Ai sensi del GDPR, hai i seguenti diritti:
          
• **Diritto di accesso**: ottenere una copia dei tuoi dati
• **Diritto di rettifica**: correggere dati inesatti
• **Diritto alla cancellazione**: eliminare i tuoi dati

Contatto: privacy@pilotleague.com`,
        },
      ],
    },
    termsOfService: {
      title: 'Termini di Servizio',
      lastUpdated: 'Ultimo aggiornamento: 12 dicembre 2025',
      sections: [
        {
          title: '1. Accettazione dei termini',
          content: `Creando un account PilotLeague, accetti questi termini di servizio.`,
        },
      ],
    },
    cookiePolicy: {
      title: 'Politica sui Cookie',
      lastUpdated: 'Ultimo aggiornamento: 12 dicembre 2025',
      sections: [
        {
          title: '1. Cosa sono i cookie?',
          content: `Un cookie è un piccolo file di testo memorizzato sul tuo dispositivo quando visiti un sito web.`,
        },
      ],
    },
  },
};

export default function LegalPage() {
  const { lang } = useParams();
  const { i18n } = useTranslation();
  const location = window.location.pathname;

  // Déterminer le type de page depuis l'URL
  const getPageType = () => {
    if (
      location.includes('confidentialite') ||
      location.includes('privacy') ||
      location.includes('datenschutz') ||
      location.includes('privacidad')
    ) {
      return 'privacyPolicy';
    }
    if (
      location.includes('conditions') ||
      location.includes('terms') ||
      location.includes('nutzungsbedingungen') ||
      location.includes('terminos') ||
      location.includes('termini')
    ) {
      return 'termsOfService';
    }
    if (location.includes('cookie')) {
      return 'cookiePolicy';
    }
    return 'privacyPolicy';
  };

  const currentLang = lang || i18n.language;
  const pageType = getPageType();
  const content =
    legalContent[currentLang]?.[pageType] ||
    legalContent['fr'][pageType] ||
    legalContent['fr'].privacyPolicy;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-50 flex flex-col">
      <header className="border-b border-slate-800 bg-slate-900/80 backdrop-blur">
        <div className="mx-auto max-w-4xl px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-amber-500 to-amber-400 flex items-center justify-center text-sm font-bold text-slate-950">
              PL
            </div>
            <span className="text-lg font-semibold">
              Pilot<span className="text-amber-500">League</span>
            </span>
          </Link>
          <Link to="/login" className="text-sm text-slate-400 hover:text-white">
            Connexion
          </Link>
        </div>
      </header>

      <main className="flex-1 mx-auto max-w-4xl px-4 py-12">
        <h1 className="text-3xl font-bold mb-2">{content.title}</h1>
        <p className="text-slate-400 mb-8">{content.lastUpdated}</p>

        <div className="space-y-8">
          {content.sections.map((section, index) => (
            <section key={index}>
              <h2 className="text-xl font-semibold mb-4 text-amber-500">{section.title}</h2>
              <div className="prose prose-invert prose-slate max-w-none">
                <p className="text-slate-300 whitespace-pre-line">{section.content}</p>
              </div>
            </section>
          ))}
        </div>

        <div className="mt-12 pt-8 border-t border-slate-800">
          <Link to="/" className="text-amber-500 hover:text-amber-400">
            ← Retour à l'accueil
          </Link>
        </div>
      </main>

      <Footer />
    </div>
  );
}
